import { EventType } from "@/analytics/analytics-events";
import { ProjectFilterEventsProperties } from "@/analytics/project-filter-events-properties";
import {
  selectHasSearchTagsOrDates,
  selectSearchEndDate,
  selectSearchStartDate,
  selectSearchTags,
  selectSearchText,
} from "@/store/project-filtering/project-filtering-selectors";
import {
  resetTagsAndDates,
  setSearchText,
  setSelectedEndDate,
  setSelectedStartDate,
  setSelectedTags,
} from "@/store/project-filtering/project-filtering-slice";
import { getTagNameSuggestions } from "@/store/project-filtering/tag-suggestions";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectTags } from "@/store/tags/tags-selectors";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { DateRangeFilter } from "../../filter-panel/filters/date-range-filter";
import { TagFilter } from "../../filter-panel/filters/tag-filter";
import { SearchFilterSortPanel } from "../../filter-panel/search-filter-sort-panel";

/**
 * @returns the filter panel for the project tree
 */
export function ProjectFilter(): JSX.Element {
  const dispatch = useAppDispatch();
  const isFilterActive = useAppSelector(selectHasSearchTagsOrDates);

  const selectedTags = useAppSelector(selectSearchTags);
  const onSelectedTagsChanged = useCallback(
    (tagIds: GUID[]) => dispatch(setSelectedTags(tagIds)),
    [dispatch],
  );

  const selectedStartDate = useAppSelector(selectSearchStartDate);
  const selectedEndDate = useAppSelector(selectSearchEndDate);
  const startDate = useMemo(
    () => (selectedStartDate ? new Date(selectedStartDate) : undefined),
    [selectedStartDate],
  );
  const endDate = useMemo(
    () => (selectedEndDate ? new Date(selectedEndDate) : undefined),
    [selectedEndDate],
  );

  const updateDateRange = useCallback(
    (start?: Date, end?: Date) => {
      // If only one date is provided, set the other to the same value
      if (start && end === undefined) end = start;
      if (end && start === undefined) start = end;

      dispatch(setSelectedStartDate(start?.toISOString()));
      dispatch(setSelectedEndDate(end?.toISOString()));
    },
    [dispatch],
  );

  // Using a local state to keep the user input responsive, while debouncing the actual search execution
  const [localSearchText, setLocalSearchText] = useState("");
  // Tracks the last value dispatched to the store so the debounce effect can skip redundant dispatches
  const lastDispatchedText = useRef("");
  useEffect(() => {
    const debounceDelay = 300;
    const delayInputTimeoutId = setTimeout(() => {
      if (localSearchText === lastDispatchedText.current) return;
      lastDispatchedText.current = localSearchText;
      dispatch(setSearchText(localSearchText));
    }, debounceDelay);
    return () => clearTimeout(delayInputTimeoutId);
  }, [localSearchText, dispatch]);

  // Follow the store when the text is set from outside this panel, which is how a
  // restored saved view arrives. Comparing against the last dispatched value is
  // what keeps this from fighting the user's own typing: while they type, the
  // store only ever catches up to what was already dispatched from here.
  const searchText = useAppSelector(selectSearchText);
  useEffect(() => {
    if (searchText === lastDispatchedText.current) return;
    lastDispatchedText.current = searchText;
    setLocalSearchText(searchText);
  }, [searchText]);

  // Tag name autocomplete suggestions shown in the search dropdown
  const tags = useAppSelector(selectTags);
  const tagSuggestions = useMemo(
    () => getTagNameSuggestions(tags, localSearchText),
    [tags, localSearchText],
  );

  const onSuggestionSelected = useCallback(
    (tagName: string) => {
      Analytics.track(EventType.selectTagSuggestion);

      lastDispatchedText.current = tagName;
      setLocalSearchText(tagName);
      dispatch(setSearchText(tagName));
    },
    [dispatch],
  );

  useTrackProjectFilterEvents(selectedTags, startDate, endDate);

  const clearFilters = useCallback(() => {
    Analytics.track(EventType.resetProjectFilters);
    dispatch(resetTagsAndDates());
  }, [dispatch]);

  return (
    <SearchFilterSortPanel
      showResetButton
      hasFilters={isFilterActive}
      onClearFilters={clearFilters}
      text={localSearchText}
      onTextChanged={setLocalSearchText}
      suggestions={tagSuggestions}
      onSuggestionSelected={onSuggestionSelected}
    >
      <TagFilter
        selectedTags={selectedTags ?? []}
        onSelectedTagsChanged={onSelectedTagsChanged}
      />

      <DateRangeFilter
        selectedStartDate={startDate}
        selectedEndDate={endDate}
        onDateRangeChange={updateDateRange}
      />
    </SearchFilterSortPanel>
  );
}

function useTrackProjectFilterEvents(
  selectedTags: GUID[] | undefined,
  startDate: Date | undefined,
  endDate: Date | undefined,
): void {
  const searchText = useAppSelector(selectSearchText);

  const hasFilterByText = !!searchText;
  const selectedTagsForFiltering = selectedTags ? selectedTags.length : 0;
  // The line below should be adapted if we decide that the start date and end date
  // should be filled at project startup with the min and max dates available
  const hasFilterByCaptureTime = !!startDate || !!endDate;

  useEffect(() => {
    if (
      hasFilterByText ||
      selectedTagsForFiltering > 0 ||
      hasFilterByCaptureTime
    ) {
      Analytics.track<ProjectFilterEventsProperties>(EventType.filterProject, {
        hasFilterByText,
        selectedTagsForFiltering,
        hasFilterByCaptureTime,
      });
    }
  }, [hasFilterByText, selectedTagsForFiltering, hasFilterByCaptureTime]);
}
